import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import urls from "../../../../global/constants/UrlConstants";
import PersonalDetails from "../stepper/Personal Details/PersonalDetails";
import YhButton from "../../../../ui/YhButton";
import { useAppSelector } from "../../../../utils/hooks";
import { selectUserId } from "../../../../redux/authSlice";
import { getFormById } from "../stepper/StepperService";
import {
  isTruthy,
  openErrorNotification,
} from "../../../../helpers/methods";
import notifiers from "../../../../global/constants/NotificationConstants";

const EDIT_STORAGE_KEY = (id: string) => `lead-form-edit-${id}`;

export type StepActions = {
  submit: () => void | Promise<void>;
};

interface CustomProps {
  isDialogOpen?: boolean;
  handleCloseAddTeam?: Function;
  userId?: string;
  idd?: string;
  setIdd?: Function;
  fromSettingOrLogout?: string;
  tableDataShowHandler?: Function;
  edit?: Boolean;
  editRowData?: any;
}

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const normalizeEditRow = (row: any) => {
  if (!row) return null;
  let dynamicFields = row.dynamicFields;
  if (typeof dynamicFields === "string") {
    try {
      dynamicFields = JSON.parse(dynamicFields);
    } catch {
      dynamicFields = [];
    }
  }
  if (!Array.isArray(dynamicFields)) dynamicFields = [];
  return { ...row, dynamicFields };
};

const AddNewTeam = (props: CustomProps = {}) => {
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const userIdFromRedux: any = useAppSelector(selectUserId);
  const isEmbedded = props.isDialogOpen !== undefined;

  const [userId] = useState<string>(props.userId || userIdFromRedux || "");
  const [idd, setIdd] = useState<string>(props.idd || id || "");
  const [editRowData, setEditRowData] = useState<any>(
    normalizeEditRow(props.editRowData) || null
  );
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const stepActionsRef = useRef<StepActions | null>(null);
  const previewRef = useRef<(() => void) | null>(null);

  const isEdit = !!(props.edit || id || props.editRowData || editRowData);
  const hasEditData = !!(editRowData || props.editRowData);

  useEffect(() => {
    if (props.idd) setIdd(props.idd);
    else if (id) setIdd(id);
  }, [props.idd, id]);

  useEffect(() => {
    let cancelled = false;

    const loadEdit = async () => {
      if (props.editRowData) {
        setEditRowData(normalizeEditRow(props.editRowData));
        return;
      }
      if (!id) {
        setEditRowData(null);
        return;
      }

      // Prefer live API so canvas always gets saved dynamicFields
      try {
        setLoadingEdit(true);
        const response = await getFormById(id);
        const row = normalizeEditRow(
          response?.data && !Array.isArray(response.data)
            ? response.data
            : Array.isArray(response?.data)
            ? response.data[0]
            : null
        );
        if (!cancelled && row) {
          setEditRowData(row);
          sessionStorage.setItem(EDIT_STORAGE_KEY(id), JSON.stringify(row));
          return;
        }
      } catch (error: any) {
        // fall through to sessionStorage
        if (!cancelled) {
          openErrorNotification(
            isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
          );
        }
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }

      try {
        const raw = sessionStorage.getItem(EDIT_STORAGE_KEY(id));
        if (!cancelled) {
          setEditRowData(raw ? normalizeEditRow(JSON.parse(raw)) : null);
        }
      } catch {
        if (!cancelled) setEditRowData(null);
      }
    };

    loadEdit();
    return () => {
      cancelled = true;
    };
  }, [id, props.editRowData]);

  const registerStepActions = useCallback((actions: StepActions) => {
    stepActionsRef.current = actions;
  }, []);

  const handlePreviewReady = useCallback((open: () => void) => {
    previewRef.current = open;
  }, []);

  const handleClose = () => {
    if (props.handleCloseAddTeam) {
      props.tableDataShowHandler?.();
      props.handleCloseAddTeam();
      return;
    }
    if (id) sessionStorage.removeItem(EDIT_STORAGE_KEY(id));
    history.push(urls.LEADFORMLIST);
  };

  const handleFooterSave = async () => {
    if (!stepActionsRef.current?.submit) return;
    try {
      setSaving(true);
      await stepActionsRef.current.submit();
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit ? "Edit Form" : "Create A New Form";
  const subtitle = isEdit
    ? "Update form details and field layout."
    : "Build a form to capture leads and manage data seamlessly.";

  const formBody = (
    <div
      className={`relative flex flex-col overflow-hidden bg-white ${
        isEmbedded
          ? "h-[min(90vh,880px)] w-full max-w-[1400px]"
          : "h-[calc(100vh-68px)] max-h-[calc(100vh-68px)]"
      }`}
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#EEF0F5] bg-white px-4 py-4 sm:px-6">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] transition hover:bg-[#F3F4F6]"
              aria-label="Back"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <h1 className="truncate text-2xl font-bold tracking-tight text-[#111827]">
              {title}
            </h1>
          </div>
          <p className="pl-10 text-sm text-[#6B7280]">{subtitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <YhButton
            variant="secondary"
            size="sm"
            startIcon={<EyeIcon />}
            onClick={() => previewRef.current?.()}
          >
            Preview
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            loading={saving}
            onClick={handleFooterSave}
          >
            {isEdit ? "Update Form" : "Save Form"}
          </YhButton>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden p-4 sm:p-5 lg:p-6">
        <div className="h-full min-h-0">
          {loadingEdit && isEdit && !hasEditData ? (
            <div className="flex h-full items-center justify-center text-sm text-[#6B7280]">
              Loading form fields…
            </div>
          ) : (
            <PersonalDetails
              userId={userId}
              edit={!!hasEditData || !!id}
              editRowData={editRowData || props.editRowData || null}
              idd={idd}
              setIdd={(v: string) => {
                setIdd(v);
                props.setIdd?.(v);
              }}
              fromSettingOrLogout={props.fromSettingOrLogout}
              handleCloseAddTeam={handleClose}
              registerStepActions={registerStepActions}
              onPreviewReady={handlePreviewReady}
              hideFooterButtons
            />
          )}
        </div>
      </div>
    </div>
  );

  if (isEmbedded) {
    if (!props.isDialogOpen) return null;
    return (
      <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-[1400px] overflow-hidden rounded-2xl shadow-xl">
          {formBody}
        </div>
      </div>
    );
  }

  return formBody;
};

export default AddNewTeam;
